/**
 * @typedef {"award" | "redeem" | "correction"} BehaviourPointsOperation
 */

/**
 * LazyLoadSelect stores students as `{ value: { id, type }, label }` or `{ id, type }`.
 * @param {unknown} value
 * @returns {number | null}
 */
export function normalizeBehaviourStudentId(value) {
  if (value == null || value === "") return null

  if (typeof value === "object") {
    const nestedId = value?.value?.id ?? value?.id
    const parsed = Number(nestedId)
    return Number.isFinite(parsed) ? parsed : null
  }

  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * @param {unknown} values
 * @returns {number[]}
 */
export function normalizeBehaviourStudentIds(values) {
  if (!Array.isArray(values)) {
    const singleId = normalizeBehaviourStudentId(values)
    return singleId != null ? [singleId] : []
  }

  return values
    .map((value) => normalizeBehaviourStudentId(value))
    .filter((id) => id != null)
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function extractFirstMessage(value) {
  if (Array.isArray(value)) {
    return String(value[0] ?? "").trim()
  }
  return String(value ?? "").trim()
}

/**
 * @param {string} apiKey
 * @param {BehaviourPointsOperation} operation
 * @returns {string | null}
 */
export function mapBehaviourPointsApiFieldToFormField(apiKey, operation) {
  const key = String(apiKey ?? "")
    .trim()
    .toLowerCase()
  if (key === "student_ids" || key.startsWith("student_ids.")) {
    return "studentIds"
  }
  if (key === "positive_behavior_id") {
    return "positiveBehaviourId"
  }
  if (key === "notes") {
    return "awardDescription"
  }
  if (key === "points") {
    return operation === "redeem" ? "pointsUsed" : "points"
  }
  if (key === "reason") {
    return operation === "redeem" ? "rewardReason" : "reason"
  }
  return null
}

/**
 * @param {Record<string, unknown>} form
 * @returns {{
 *   positive_behavior_id: number
 *   student_ids: number[]
 *   notes?: string
 * }}
 */
export function buildAwardPointsPayload(form) {
  const studentIds = normalizeBehaviourStudentIds(form?.studentIds)
  const positiveBehaviorId = Number(form?.positiveBehaviourId)
  const notes = String(form?.awardDescription ?? "").trim()

  const payload = {
    positive_behavior_id: positiveBehaviorId,
    student_ids: studentIds
  }

  if (notes) {
    payload.notes = notes
  }

  return payload
}

/**
 * @param {Record<string, unknown>} form
 * @returns {{
 *   points: number
 *   student_ids: number[]
 *   reason: string
 * }}
 */
export function buildRedeemPointsPayload(form) {
  return {
    points: Number(form?.pointsUsed),
    student_ids: normalizeBehaviourStudentIds(form?.studentIds),
    reason: String(form?.rewardReason ?? "").trim()
  }
}

/**
 * @param {Record<string, unknown>} form
 * @returns {{
 *   points: number
 *   student_ids: number[]
 *   reason: string
 * }}
 */
export function buildCorrectionPointsPayload(form) {
  return {
    points: Number(form?.points),
    student_ids: normalizeBehaviourStudentIds(form?.studentIds),
    reason: String(form?.reason ?? "").trim()
  }
}

/**
 * @param {unknown} err
 * @param {BehaviourPointsOperation} operation
 * @returns {{ fieldErrors: Record<string, string>, message: string }}
 */
export function parseBehaviourPointsApiError(err, operation) {
  const data = err?.response?.data
  const fieldErrors = {}

  const errorsObj = data?.errors
  if (errorsObj && typeof errorsObj === "object") {
    Object.entries(errorsObj).forEach(([apiKey, messages]) => {
      const formField = mapBehaviourPointsApiFieldToFormField(apiKey, operation)
      if (!formField) return
      const message = extractFirstMessage(messages)
      if (message && !fieldErrors[formField]) {
        fieldErrors[formField] = message
      }
    })
  }

  const message =
    String(data?.error ?? data?.message ?? err?.message ?? "").trim() || ""

  return { fieldErrors, message }
}

/**
 * @param {Record<string, unknown> | null | undefined} body
 * @returns {{ transactionIds: number[], count: number }}
 */
export function parseBehaviourPointsSubmitResponse(body) {
  const transactionIds = (body?.transaction_ids ?? [])
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id))

  return {
    transactionIds,
    count: Number(body?.count) || transactionIds.length
  }
}
